import { useState } from 'react'

import {
  Box,
  Colors,
  FontsSizes,
  FontsWeights,
  Icon,
  Text,
  WhiteSpace,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Maybe } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/project_management/components/Amount'
import LaborCostSourceTag from 'modules/project_management/components/labelTags/LaborCostSourceTag'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'

const defaultProps = (): {
  size: FontsSizes
  weight: FontsWeights
  color: Colors
} => ({
  size: '200',
  weight: 'medium',
  color: 'grey1000',
})

const defaultInnerProps = (): {
  size: FontsSizes
  weight: FontsWeights
  color: Colors
  whiteSpace: WhiteSpace
} => ({
  size: '200',
  weight: 'normal',
  color: 'grey800',
  whiteSpace: 'breakSpaces',
})

interface ProjectWorkerCardProps {
  laborCostCents: Maybe<number> | undefined
  laborCostCurrency: string
  originalCostCents: Maybe<number> | undefined
  originalCostCurrency: Maybe<string> | undefined
  imputedMinutes: Maybe<number> | undefined
  exchangeDate: Maybe<string> | undefined
  yearMonth: Maybe<string> | undefined
}

const ProjectWorkerCard = ({
  laborCostCents,
  laborCostCurrency,
  originalCostCents,
  originalCostCurrency,
  imputedMinutes,
  exchangeDate,
  yearMonth,
}: ProjectWorkerCardProps) => {
  const [isHovering, toggleHovering] = useToggle(false)
  const [openDetails, toggleOpenDetails] = useState<boolean>(false)

  const onClickDetails = (event: MouseEvent) => {
    toggleOpenDetails(!openDetails)
    event.stopPropagation()

    track(trackingEvents.projectManagement.projectWorkers.openProjectWorkerLaborCostMonthDetail, {
      year_month: yearMonth,
    })
  }

  const hourlyCostCents =
    laborCostCents && imputedMinutes && Math.round(laborCostCents / (imputedMinutes / 60))

  const isExchangedAmount = originalCostCurrency !== laborCostCurrency
  const exchangeRate =
    originalCostCents && laborCostCents && (originalCostCents / laborCostCents).toFixed(5)

  return (
    <Box
      border={{ all: { width: 's2', style: 'solid', color: 'grey400' } }}
      borderRadius={{ all: 'abs016' }}
      background={isHovering ? 'grey200' : 'grey000White'}
      onMouseEnter={toggleHovering(true)}
      onMouseLeave={toggleHovering(false)}
    >
      <Box
        padding="s16"
        gap="s8"
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        onClick={onClickDetails}
      >
        <Box flexDirection="column" width="full" gap="s8">
          {openDetails && (
            <>
              {isExchangedAmount && (
                <>
                  {exchangeDate && (
                    <Box flexDirection="row">
                      <Text {...defaultInnerProps()}>
                        {i18n.t('project_management.project_workers.details.exchange_date') + ': '}
                      </Text>
                      <Text {...defaultProps()}>{dateBackendToUI(exchangeDate)}</Text>
                    </Box>
                  )}
                  <Box flexDirection="row">
                    <Text {...defaultInnerProps()}>
                      {i18n.t('project_management.project_workers.details.exchange_rate') + ': '}
                    </Text>
                    <Text {...defaultProps()}>
                      {exchangeRate + ` ${originalCostCurrency} / ${laborCostCurrency}`}
                    </Text>
                  </Box>
                </>
              )}
              <Box flexDirection="row">
                <Text {...defaultInnerProps()}>
                  {i18n.t('project_management.project_workers.details.cost_per_hour') + ': '}
                </Text>
                <Amount amount={hourlyCostCents} currency={laborCostCurrency} {...defaultProps()} />
              </Box>
              <Box flexDirection="row">
                <Text {...defaultInnerProps()}>
                  {i18n.t('project_management.project_workers.details.registered_hours') + ': '}
                </Text>
                <Text {...defaultProps()}>{formatProjectHours(imputedMinutes)}</Text>
              </Box>
              {isExchangedAmount && (
                <>
                  <Box flexDirection="row">
                    <Text {...defaultInnerProps()}>
                      {i18n.t('project_management.project_workers.details.original_cost', {
                        currency: originalCostCurrency,
                      }) + ': '}
                    </Text>
                    {originalCostCurrency && (
                      <Amount
                        amount={originalCostCents}
                        currency={originalCostCurrency}
                        {...defaultProps()}
                      />
                    )}
                  </Box>
                </>
              )}
            </>
          )}
          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box flexDirection="row">
              <Text {...defaultInnerProps()}>
                {i18n.t('project_management.project_workers.details.total_cost') + ': '}
              </Text>
              <Amount amount={laborCostCents} currency={laborCostCurrency} {...defaultProps()} />
            </Box>
          </Box>
        </Box>
        <Box flexDirection="column" height="full" alignItems="flexStart">
          <Box flexDirection="row" alignItems="center" gap="s16">
            <LaborCostSourceTag laborCostSource="estimated" />
            <Icon.Small icon={openDetails ? 'chevron-up' : 'chevron-down'} color="grey800" />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ProjectWorkerCard
